import { HttpClient } from "services/HttpClient";
import {
  Patient,
  PatientDailyCheckIn,
  FormattedResponseJointType,
} from "types/Patient";
import { getData } from "../contexts/authContext";
import {
  CreatePatientPayload,
  PatientDailyCheckInQuery,
  TeamLead,
  TeamMembers,
  UpdatePatientPayload,
} from "./Models/types";
import { ApiPaginatedResponse, CommonApiParams } from "types/common";
import { RecoveryPlanType } from "types/RecoveryPlan";
import * as cognito from "../libs/cognito";

export class PatientRepository extends HttpClient {
  public getPatient = async (patientId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<Patient>(`/patient/${patientId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public getPatientsForCurrentClinic = async (
    params: CommonApiParams & {
      search?: string;
      inviteDate?: string;
      authStatus?: string;
    }
  ) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<ApiPaginatedResponse<Patient>>(
      `/patient`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public assignTeamMembers = async (
    patientId: string,
    payload: TeamMembers
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/patient/${patientId}/assignment`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public assignTeamLead = async (patientId: string, payload: TeamLead) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/patient/${patientId}/assignment`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public createPatientOnInvite = async (payload: CreatePatientPayload) => {
    const token = await getData("idToken");

    const { data } = await this.instance.post(`/patient`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public updatePatient = async (
    patientId: string,
    payload: UpdatePatientPayload
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/patient/${patientId}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getPatientDailyCheckinData = async (
    patientId: string,
    params: PatientDailyCheckInQuery
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<PatientDailyCheckIn>(
      `/patient/${patientId}/daily-check-in/`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public createRecoveryPlan = async (
    payload: Omit<RecoveryPlanType, "_id">
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.post<RecoveryPlanType>(
      `/recovery-plan`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public updateRecoveryPlan = async (
    recoveryPlanId: string,
    payload: Omit<RecoveryPlanType, "_id">
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch<RecoveryPlanType>(
      `/recovery-plan/${recoveryPlanId}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public deleteRecoveryPlan = async (recoveryPlanId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete<RecoveryPlanType>(
      `/recovery-plan/${recoveryPlanId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public getJointData = async (patientId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<FormattedResponseJointType>(
      `/patient/${patientId}/joint-data`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  constructor() {
    super(process.env.REACT_APP_BASE_URL as string,()=>{
      cognito.signOut(true)
    });
  }

  private static classInstance?: PatientRepository;

  public static getInstance() {
    if (!this.classInstance) {
      this.classInstance = new PatientRepository();
    }
    return this.classInstance;
  }
}
